import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  Segment,
  Button,
  Menu,
  Table,
  Dimmer,
  Loader,
  Dropdown,
} from 'semantic-ui-react';
import { getReadingScriptStatistics } from '../api/ReadingScriptStatisticsApi';
import { ReadingScriptStatisticsDTO } from '../dto/ReadingScriptStatisticsDTOs';
import { getAccessToken } from '../common/Configuration';

const ReadingScriptStatisticsPage: React.FC = () => {
  
  // 날짜 포맷팅 함수 (YYYY-MM-DD)
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 내일 날짜 계산
  const getTomorrow = (): string => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return formatDate(tomorrow);
  };

  // N일 전 날짜 계산
  const getDaysAgo = (days: number): string => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return formatDate(date);
  };

  // 오늘 날짜 계산
  const getToday = (): string => {
    return formatDate(new Date());
  };

  // 초기 날짜 계산
  const getInitialStartDate = (): string => {
    return getDaysAgo(7);
  };

  const getInitialEndDate = (): string => {
    return getTomorrow();
  };

  const [loading, setLoading] = useState(true);
  const [statisticsData, setStatisticsData] = useState<ReadingScriptStatisticsDTO | null>(null);
  const [startDate, setStartDate] = useState(getInitialStartDate());
  const [endDate, setEndDate] = useState(getInitialEndDate());
  const [selectedPeriod, setSelectedPeriod] = useState('7days');
  const [dateInputEnabled, setDateInputEnabled] = useState(false);
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<string | undefined>('all');

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
    
    if (period === 'today') {
      setDateInputEnabled(false);
      setStartDate(getToday());
      setEndDate(getTomorrow());
    } else if (period === '7days') {
      setDateInputEnabled(false);
      setStartDate(getDaysAgo(7));
      setEndDate(getTomorrow());
    } else if (period === '30days') {
      setDateInputEnabled(false);
      setStartDate(getDaysAgo(30));
      setEndDate(getTomorrow());
    } else if (period === 'dateInput') {
      setDateInputEnabled(true);
    }
  };

  const ageGroupOptions = [
    { key: 'all', text: '전체', value: 'all' },
    { key: '10s', text: '10대', value: '10s' },
    { key: '20s30s', text: '20,30대', value: '20s30s' },
    { key: '40s50s', text: '40,50대', value: '40s50s' },
    { key: '60s', text: '60대이상', value: '60s' },
  ];

  const loadStatisticsData = async () => {
    setLoading(true);
    try {
      const {accessToken, refreshToken} = await getAccessToken();// as {accessToken: string, refreshToken: string};
      const result = await getReadingScriptStatistics(
        accessToken || '',
        startDate,
        endDate
      );
      if (result && result.errorCode === 0) {
        setStatisticsData(result.response);
      }
    } catch (error) {
      console.error('독서·필사 통계 데이터 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatisticsData();
  }, [startDate, endDate]);

  const handleSearch = () => {
    loadStatisticsData();
  };

  if (loading) {
    return (
      <Container fluid>
        <Dimmer active inverted>
          <Loader size="large">데이터를 불러오는 중...</Loader>
        </Dimmer>
      </Container>
    );
  }

  if (!statisticsData) {
    return (
      <Container fluid>
        <Header as="h2">데이터를 불러올 수 없습니다.</Header>
      </Container>
    );
  }

  return (
    <Container fluid>
      {/* 상단 제목 */}
      <Header as="h2" style={{ marginBottom: 16 }}>
        2. 독서·필사 통계
      </Header>

      {/* 기간 선택 + 상단 탭 */}
      <Segment
        raised
        style={{
          padding: '16px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#f8f9ff',
          marginBottom: 24,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ marginRight: 8, fontWeight: 500 }}>시작일</span>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            disabled={!dateInputEnabled}
            style={{
              padding: '6px 10px',
              borderRadius: 4,
              border: '1px solid #d0d4e4',
              backgroundColor: dateInputEnabled ? '#fff' : '#f5f5f5',
              cursor: dateInputEnabled ? 'text' : 'not-allowed',
            }}
          />
          <span style={{ margin: '0 4px' }}>~</span>
          <span style={{ marginRight: 8, fontWeight: 500 }}>종료일</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            disabled={!dateInputEnabled}
            style={{
              padding: '6px 10px',
              borderRadius: 4,
              border: '1px solid #d0d4e4',
              backgroundColor: dateInputEnabled ? '#fff' : '#f5f5f5',
              cursor: dateInputEnabled ? 'text' : 'not-allowed',
            }}
          />
          <Button primary style={{ marginLeft: 12 }} onClick={handleSearch}>
            검색
          </Button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* 기간 선택 버튼 */}
          <Menu compact>
            <Menu.Item
              active={selectedPeriod === 'today'}
              onClick={() => handlePeriodChange('today')}
            >
              오늘
            </Menu.Item>
            <Menu.Item
              active={selectedPeriod === '7days'}
              onClick={() => handlePeriodChange('7days')}
            >
              7일
            </Menu.Item>
            <Menu.Item
              active={selectedPeriod === '30days'}
              onClick={() => handlePeriodChange('30days')}
            >
              30일
            </Menu.Item>
            <Menu.Item
              active={selectedPeriod === 'dateInput'}
              onClick={() => handlePeriodChange('dateInput')}
            >
              날짜입력
            </Menu.Item>
          </Menu>

          {/* 연령대별 드롭다운 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 500 }}>연령대별:</span>
            <Dropdown
              selection
              options={ageGroupOptions}
              value={selectedAgeGroup}
              onChange={(e, { value }) => setSelectedAgeGroup(value as string | undefined)}
              style={{ minWidth: 120 }}
            />
          </div>
        </div>
      </Segment>

      {/* 테이블 영역 */}
      <div style={{ display: 'flex', gap: 24, alignItems: 'stretch' }}>
        {/* 왼쪽 테이블: 인기 도서 TOP 10 */}
        <Segment raised style={{ flex: 1, padding: '20px', margin: 0 }}>
          <Header
            as="h4"
            style={{
              marginBottom: 16,
              marginTop: 0,
              minHeight: '28px',
              height: '28px',
              lineHeight: '28px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            인기 도서 TOP 10
          </Header>
          <Table celled style={{ marginTop: 0 }}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>순위</Table.HeaderCell>
                <Table.HeaderCell>도서명</Table.HeaderCell>
                <Table.HeaderCell>작가</Table.HeaderCell>
                <Table.HeaderCell>읽은수</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {statisticsData.popularBooks.map((book) => (
                <Table.Row key={book.rank}>
                  <Table.Cell>{book.rank}</Table.Cell>
                  <Table.Cell>{book.bookTitle}</Table.Cell>
                  <Table.Cell>{book.author}</Table.Cell>
                  <Table.Cell>{book.userCount.toLocaleString()}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Segment>

        {/* 오른쪽 테이블: 인기 필사 도서 TOP 10 */}
        <Segment raised style={{ flex: 1, padding: '20px', margin: 0 }}>
          <Header
            as="h4"
            style={{
              marginBottom: 16,
              marginTop: 0,
              minHeight: '28px',
              height: '28px',
              lineHeight: '28px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            인기 필사 도서 TOP 10
          </Header>
          <Table celled style={{ marginTop: 0 }}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>순위</Table.HeaderCell>
                <Table.HeaderCell>도서명</Table.HeaderCell>
                <Table.HeaderCell>작가</Table.HeaderCell>
                <Table.HeaderCell>필사수</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {statisticsData.popularScriptBooks.map((book) => (
                <Table.Row key={book.rank}>
                  <Table.Cell>{book.rank}</Table.Cell>
                  <Table.Cell>{book.bookTitle}</Table.Cell>
                  <Table.Cell>{book.author}</Table.Cell>
                  <Table.Cell>{book.userCount.toLocaleString()}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Segment>
      </div>
    </Container>
  );
};

export default ReadingScriptStatisticsPage;
